import type { ChangeEvent, ComponentProps } from "react"
import { useCaretRestore } from "../hooks/use-caret-restore.ts"
import { TRACKING_MAX_DIGITS, editTracking, formatTracking } from "../lib/tracking.ts"
import { Input } from "../ui/index.ts"

/** 12자리 + 하이픈 둘. 붙여넣기로도 이보다 길게 들어오지 않게 한다. */
const MAX_LENGTH = TRACKING_MAX_DIGITS + 2

/**
 * 반송장번호 입력칸.
 *
 * 숫자만 받아 네 자리마다 하이픈을 붙여 보여 주고(`4954-1075-9314`), 값은 숫자만
 * (`495410759314`) 돌려준다 — 손으로 치든 붙여넣든 같다. 하이픈은 사람이 칠 필요가 없다.
 */
export function TrackingNoInput({
  value,
  onChange,
  ...rest
}: {
  /** 숫자만 담긴 값. */
  value: string
  /** 숫자만 담긴 새 값. */
  onChange: (digits: string) => void
} & Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type">) {
  const { ref, setCaret } = useCaretRestore<HTMLInputElement>()

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target
    const inputType = (event.nativeEvent as InputEvent).inputType ?? ""
    const result = editTracking({
      value: input.value,
      caret: input.selectionStart ?? input.value.length,
      deleting: inputType.startsWith("deleteContentBackward")
        ? "backward"
        : inputType.startsWith("deleteContentForward")
          ? "forward"
          : null,
      previousDigits: value,
    })

    if (result.digits === value) {
      // 숫자가 그대로면(숫자 아닌 글자·13번째 자리) 다시 그리지 않으므로 입력칸을 직접 되돌린다.
      input.value = result.formatted
      input.setSelectionRange(result.caret, result.caret)
      return
    }

    setCaret(result.caret)
    onChange(result.digits)
  }

  return (
    <Input
      ref={ref}
      inputMode="numeric"
      autoComplete="off"
      maxLength={MAX_LENGTH}
      value={formatTracking(value) ?? ""}
      onChange={handleChange}
      {...rest}
    />
  )
}
